import Actions, {
  getPreviousItemAndSong,
  getNextItemAndSong,
  getNextItemFromItems,
  getFirstSongFromItem,
  isRelease,
  isPlaylist,
  isSong,
} from 'reducers/PlayQueue'
import PlayerActions, { thunks as PlayerThunks } from 'reducers/Player'
import { thunks as ReleaseThunks } from 'reducers/Releases'
import { storeHelpers } from 'utils'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import first from 'lodash/first'
import type { AppDispatch } from 'types/store'
import type { PlaySource } from 'reducers/Player/thunks'

// Removing the playing song from the queue moves playback to the next one
const REMOVE_CURRENT_PLAY_SOURCE: PlaySource = {
  source: PLAY_SOURCES.QUEUE,
  trigger: PLAY_TRIGGERS.REMOVE_CURRENT
}

const playRelease =
  (releaseId: string, playSource?: PlaySource) => (dispatch: AppDispatch) => {
    dispatch(PlayerThunks.playSampleSongOnSafari() as any)
    dispatch(
      ReleaseThunks.getDetail(releaseId, (detailedItem: any) => {
        const normalizedItem = detailedItem.songs
          ? {
              ...detailedItem,
              songs: detailedItem.songs.filter(
                (s: any) => s.assettype === 'audio'
              )
            }
          : detailedItem

        dispatch(Actions.insertItemAtTop(normalizedItem))
        const firstSong = first(normalizedItem.songs)
        dispatch(PlayerThunks.playSong(firstSong, playSource) as any)
      }) as any
    )
  }

const playPlaylist =
  (playlist: any, playSource?: PlaySource) => (dispatch: AppDispatch) => {
    dispatch(Actions.setPlaylist(playlist))
    if (playlist.songs.length > 0) {
      const firstSong = first(playlist.songs)
      dispatch(PlayerThunks.playSong(firstSong, playSource) as any)
    }
  }

const insertReleaseNext = (releaseId: string) => (dispatch: AppDispatch) => {
  dispatch(
    ReleaseThunks.getDetail(releaseId, (detailedItem: any) => {
      dispatch(Actions.insertItemNext(detailedItem))
    }) as any
  )
}

const insertReleaseAtBottom = (releaseId: string) => (dispatch: AppDispatch) => {
  dispatch(
    ReleaseThunks.getDetail(releaseId, (detailedItem: any) => {
      dispatch(Actions.insertItemAtBottom(detailedItem))
    }) as any
  )
}

const playSong =
  (song: any, playSource?: PlaySource) => (dispatch: AppDispatch) => {
    dispatch(Actions.insertItemAtTop(song))
    dispatch(PlayerThunks.playSong(song, playSource) as any)
  }

const seekPrevious =
  (state: any, playSource?: PlaySource) => (dispatch: AppDispatch) => {
    const { song } = getPreviousItemAndSong(state)

    if (song) {
      // Play the previous song
      dispatch(PlayerThunks.playSong(song, playSource) as any)

      // Update the state to reflect the change
      dispatch(Actions.seekPrevious())
    }
  }

const seekNext =
  (state: any, playSource?: PlaySource) => (dispatch: AppDispatch) => {
    const { song } = getNextItemAndSong(state)
    dispatch(PlayerThunks.playSong(song, playSource) as any)
    dispatch(Actions.seekNext())
  }

const seekToItem =
  (item: any, playSource?: PlaySource) => (dispatch: AppDispatch) => {
    dispatch(Actions.seekToItem(item))
    const song = getFirstSongFromItem(item)
    if (song) {
      dispatch(PlayerThunks.playSong(song, playSource) as any)
    }
  }

const deleteRelease =
  (
    release: any,
    state: any,
    isPlaying: boolean,
    playSource: PlaySource = REMOVE_CURRENT_PLAY_SOURCE
  ) =>
  (dispatch: AppDispatch) => {
    const { selectedItem, items } = state
    if (selectedItem && release.uuid === selectedItem.uuid) {
      const nextItem = getNextItemFromItems(selectedItem, items)
      if (isPlaying && nextItem) {
        dispatch(seekToItem(nextItem, playSource) as any)
      } else {
        dispatch(PlayerActions.resetPlayer())
      }
    }
    dispatch(Actions.deleteItem(release))
  }

const deleteSong =
  (
    song: any,
    state: any,
    isPlaying: boolean,
    playSource: PlaySource = REMOVE_CURRENT_PLAY_SOURCE
  ) =>
  (dispatch: AppDispatch) => {
    const { selectedSong } = state
    dispatch(Actions.deleteItem(song))

    if (
      selectedSong &&
      (song.uuid === selectedSong.uuid || song.trackid === selectedSong.trackid)
    ) {
      const { song: nextSong } = getNextItemAndSong(state)
      if (isPlaying && nextSong?.trackid) {
        dispatch(seekNext(state, playSource) as any)
      } else {
        dispatch(PlayerActions.resetPlayer())
      }
    }
  }

const deleteItem =
  (item: any, state: any, isPlaying: boolean) => (dispatch: AppDispatch) => {
    if (isRelease(item) || isPlaylist(item)) {
      dispatch(deleteRelease(item, state, isPlaying) as any)
    } else if (isSong(item)) {
      dispatch(deleteSong(item, state, isPlaying) as any)
    }
  }

const clearQueue = () => (dispatch: AppDispatch) => {
  dispatch(PlayerActions.resetPlayer())
  dispatch(Actions.clearQueue())
}

const deletePlaylist = (playlist: any) => (dispatch: AppDispatch) => {
  storeHelpers.asyncGetStoreState().then(({ playQueue }: any) => {
    const firstItem = first(playQueue.items) as any
    if (firstItem && firstItem.playlistid === playlist.playlistid) {
      dispatch(PlayerActions.resetPlayer())
      dispatch(Actions.resetPlayQueue())
    }
  })
}

const updatePlaylist = (playlist: any) => (dispatch: AppDispatch) => {
  storeHelpers.asyncGetStoreState().then(({ playQueue }: any) => {
    const firstItem = first(playQueue.items) as any
    if (firstItem && firstItem.playlistid === playlist.playlistid) {
      dispatch(Actions.setPlaylist(playlist))
    }
  })
}

export default {
  playRelease,
  playSong,
  clearQueue,
  seekPrevious,
  seekNext,
  seekToItem,
  insertReleaseNext,
  insertReleaseAtBottom,
  playPlaylist,
  deletePlaylist,
  updatePlaylist,
  deleteItem,
  deleteSong,
}
